/**
 * Drawing primitives for the Chart canvas.
 *
 * Drawings are stored in domain coordinates (`{x: candleIndex, y: price}`) so
 * they survive timeframe + chart-type changes. The Chart converts to SVG
 * coordinates at render time.
 *
 * Persistence: localStorage keyed by (symbol, setName). A `BrokerClient` hook
 * can be wired later to sync sets to the BAL for cross-device persistence;
 * the load/save signatures are async so swapping is one-line.
 */

export type DrawingKind =
  | 'trendline'    // 2 pts — straight line
  | 'price'        // 1 pt  — horizontal "Price Level"
  | 'time'         // 1 pt  — vertical "Time Level"
  | 'rect'         // 2 pts — rectangle
  | 'text'         // 1 pt  — text annotation (carries .label)
  | 'fibRetrace'   // 2 pts — fibonacci retracements
  | 'fibExtend'    // 3 pts — fibonacci extensions
  | 'channel';     // 3 pts — parallel-trend channel (2 base + 1 offset)

export interface Point { x: number; y: number; }

export interface Drawing {
  id: string;
  kind: DrawingKind;
  pts: Point[];
  /** Used by 'text' kind. */
  label?: string;
  /** Optional override; defaults to drawing-tool stroke color. */
  color?: string;
}

/** How many points each tool needs before it is committed. */
export const POINTS_REQUIRED: Record<DrawingKind, number> = {
  trendline: 2,
  price: 1,
  time: 1,
  rect: 2,
  text: 1,
  fibRetrace: 2,
  fibExtend: 3,
  channel: 3,
};

/** UI labels — match the Drawings popover screenshot exactly. */
export const TOOL_LABELS: Record<DrawingKind, string> = {
  trendline: 'Trendline',
  price: 'Price Level',
  time: 'Time Level',
  fibRetrace: 'Fibonacci Retracements',
  fibExtend: 'Fibonacci Extensions',
  rect: 'Rectangle',
  text: 'Text',
  channel: 'Channel',
};

/** Compact glyphs displayed left of the tool name. */
export const TOOL_GLYPHS: Record<DrawingKind, string> = {
  trendline: '/',
  price: '$',
  time: '⏱',
  fibRetrace: '%',
  fibExtend: '↗',
  rect: '▭',
  text: 'T',
  channel: '∥',
};

export const TOOL_ORDER: DrawingKind[] = [
  'trendline', 'price', 'time', 'fibRetrace', 'fibExtend', 'rect', 'text', 'channel',
];

/** Standard Fibonacci ratios used for retracement levels (0..1 between high and low). */
export const FIB_RETRACE_LEVELS: ReadonlyArray<{ ratio: number; label: string; color: string }> = [
  { ratio: 0,     label: '0.0%',   color: '#fbbf24' },
  { ratio: 0.236, label: '23.6%',  color: '#a78bfa' },
  { ratio: 0.382, label: '38.2%',  color: '#3dd6ff' },
  { ratio: 0.500, label: '50.0%',  color: '#3ddc84' },
  { ratio: 0.618, label: '61.8%',  color: '#fbbf24' },
  { ratio: 0.786, label: '78.6%',  color: '#fb923c' },
  { ratio: 1.000, label: '100.0%', color: '#ff6b6b' },
];

/** Extension levels measured from the C-point in a 3-point fib extension. */
export const FIB_EXTEND_LEVELS: ReadonlyArray<{ ratio: number; label: string; color: string }> = [
  { ratio: 0,     label: '0.0%',   color: '#fbbf24' },
  { ratio: 0.382, label: '38.2%',  color: '#3dd6ff' },
  { ratio: 0.618, label: '61.8%',  color: '#3ddc84' },
  { ratio: 1.000, label: '100.0%', color: '#fbbf24' },
  { ratio: 1.382, label: '138.2%', color: '#fb923c' },
  { ratio: 1.618, label: '161.8%', color: '#ec4899' },
  { ratio: 2.000, label: '200.0%', color: '#ff6b6b' },
  { ratio: 2.618, label: '261.8%', color: '#a78bfa' },
];

/** For each retracement level, return the actual price between high and low. */
export function fibRetracePrices(high: number, low: number): Array<{ ratio: number; label: string; color: string; price: number }> {
  return FIB_RETRACE_LEVELS.map(({ ratio, label, color }) => ({
    ratio, label, color,
    price: high - (high - low) * ratio,
  }));
}

/**
 * Fib extension is computed from the projected leg (B−A), measured FROM C.
 *  - leg = B − A
 *  - level price = C + leg * ratio (when B > A; the function works either direction)
 */
export function fibExtendPrices(a: number, b: number, c: number): Array<{ ratio: number; label: string; color: string; price: number }> {
  const leg = b - a;
  return FIB_EXTEND_LEVELS.map(({ ratio, label, color }) => ({
    ratio, label, color, price: c + leg * ratio,
  }));
}

/**
 * Channel: given two base points (the trendline) and an offset point, returns
 * the parallel line endpoints in domain coords.
 *  - base goes through p1 → p2
 *  - offsetDelta is computed from the perpendicular distance of p3 to base
 *  - parallel line = base shifted by offsetDelta on the y axis (we only use
 *    horizontal-time charts so vertical delta is the visually correct shift)
 */
export function channelLines(p1: Point, p2: Point, p3: Point): { base: [Point, Point]; parallel: [Point, Point] } {
  if (p2.x === p1.x) {
    // Degenerate: identical x; render a horizontal pair.
    return { base: [p1, p2], parallel: [{ x: p1.x, y: p3.y }, { x: p2.x, y: p3.y }] };
  }
  const slope = (p2.y - p1.y) / (p2.x - p1.x);
  const baseYAtP3X = p1.y + slope * (p3.x - p1.x);
  const dy = p3.y - baseYAtP3X;
  const parallel: [Point, Point] = [{ x: p1.x, y: p1.y + dy }, { x: p2.x, y: p2.y + dy }];
  return { base: [p1, p2], parallel };
}

// ---------- persistence (localStorage by default) ----------

export interface DrawingSetIO {
  list(symbol: string): Promise<string[]>;
  load(symbol: string, setName: string): Promise<Drawing[]>;
  save(symbol: string, setName: string, drawings: Drawing[]): Promise<void>;
  remove(symbol: string, setName: string): Promise<void>;
}

const KEY_PREFIX = 'nigastock.drawings.';
function key(symbol: string, setName: string): string {
  return `${KEY_PREFIX}${symbol}.${setName}`;
}
const INDEX_KEY = (symbol: string) => `${KEY_PREFIX}${symbol}.__sets`;

export const localDrawingStore: DrawingSetIO = {
  async list(symbol) {
    if (typeof localStorage === 'undefined') return ['Default'];
    const raw = localStorage.getItem(INDEX_KEY(symbol));
    if (!raw) return ['Default'];
    try { const arr = JSON.parse(raw); return Array.isArray(arr) && arr.length ? arr : ['Default']; }
    catch { return ['Default']; }
  },
  async load(symbol, setName) {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(key(symbol, setName));
    if (!raw) return [];
    try { const v = JSON.parse(raw); return Array.isArray(v) ? v as Drawing[] : []; }
    catch { return []; }
  },
  async save(symbol, setName, drawings) {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key(symbol, setName), JSON.stringify(drawings));
    const sets = await this.list(symbol);
    if (!sets.includes(setName)) {
      sets.push(setName);
      localStorage.setItem(INDEX_KEY(symbol), JSON.stringify(sets));
    }
  },
  async remove(symbol, setName) {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(key(symbol, setName));
    const sets = (await this.list(symbol)).filter((s) => s !== setName);
    localStorage.setItem(INDEX_KEY(symbol), JSON.stringify(sets.length ? sets : ['Default']));
  },
};

export function newDrawingId(): string {
  const arr = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(arr);
  else for (let i = 0; i < 8; i++) arr[i] = Math.floor(Math.random() * 256);
  return 'd_' + Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}
